/**
 * useRobiChat — shared Robi conversation logic.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  generateRobiMessageId,
  ROBI_CHAT_USER,
  STORAGE_KEYS,
} from '../constants';
import { messageService } from '../api';
import { useProfile } from '../context/ProfileContext';
import { ToastService } from '../platform/notifier';
import { analytics, ANALYTICS_EVENTS } from '../analytics';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { ImageSource } from '../types';

import type { ExtendedChatMessage } from '../types/chat';

export interface UseRobiChatOptions {
  /** Bundled or remote avatar for the assistant, supplied by the UI layer. */
  assistantAvatar?: ImageSource | string | null;
  conversationId?: number | null;
}

export interface UseRobiChatResult {
  messages: ExtendedChatMessage[];
  currentUser: { _id: string | number; name: string };
  handleSend: (newMessages?: ExtendedChatMessage[]) => void;
  handleReactionSelect: (messageId: string | number, reaction: string) => void;
  handleDoubleTap: (msg: ExtendedChatMessage) => void;
  isSendingMessage: boolean;
}

const useRobiChat = ({
  assistantAvatar: _assistantAvatar,
  conversationId,
}: UseRobiChatOptions = {}): UseRobiChatResult => {
  const { profile } = useProfile();

  const currentUser = useMemo(() => ({
    _id: profile?.userId ?? 'user-1',
    name: profile?.firstName ?? 'You',
  }), [profile?.userId, profile?.firstName]);

  const MESSAGES_PAGE_SIZE = 20;
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const followedAgent = profile?.followedAiAgent;
  const agentId = followedAgent?.agentId;
  const agentName = followedAgent?.userName;
  const agentProfilePicture = followedAgent?.profilePicture;
  const userName = profile?.userName;
  useEffect(() => {
    let mounted = true;
    if (conversationId) {
      messageService.getMessages({ 
        conversationId, 
        page: 1, 
        pageSize: MESSAGES_PAGE_SIZE,
        currentUserId: profile?.userId 
      })
        .then(res => {
          if (!mounted || !res) return;
          if (res.messages && res.messages.length > 0) {
            setMessages(res.messages);
          } else {
            const newGreetingText = `Hi ${userName || 'AI Buddy'}, and welcome to WLB's natural language AI.\nI can help you do so many things, like plan a diet, suggest an exercise routine, hold you accountable, or just chat.\nIf you choose the option in AI settings to "keep history for 30 days", I can better customize my answers to suit your needs.\nAt any time in your settings, you can choose to "erase all history", which will erase our past chat history.`;
            
            let resolvedGreetingText = newGreetingText;

            if (mounted) {
              const greetingMessage: ExtendedChatMessage = {
                _id: `greeting-${conversationId}`,
                text: resolvedGreetingText,
                createdAt: new Date(),
                user: {
                  _id: agentId || ROBI_CHAT_USER._id,
                  name: agentName || 'AI Buddy',
                  avatar: _assistantAvatar || agentProfilePicture || null,
                },
                system: false,
              };
              setMessages([greetingMessage]);
            }
          }
        })
        .catch(err => {
          console.error('Failed to fetch Robi Chat messages:', err);
        });
    } else {
      setMessages([]);
    }
    return () => { mounted = false; };
  }, [conversationId, profile?.userId, agentId, agentName, agentProfilePicture, _assistantAvatar, userName]);

  const handleSend = useCallback(
    (newMessages: ExtendedChatMessage[] = []) => {
      const userMessage = newMessages[0];
      if (!userMessage || !conversationId || isSendingMessage) return;

      const optimisticId = generateRobiMessageId();
      setMessages(prev => [
        {
          _id: optimisticId,
          text: userMessage.text,
          image: userMessage.image,
          imageMetadata: userMessage.imageMetadata,
          replyTo: userMessage.replyTo,
          createdAt: new Date(),
          user: currentUser,
        },
        ...prev,
      ]);

      setIsSendingMessage(true);
      messageService
        .sendMessage({
          conversationId,
          input: {
            text: userMessage.text,
            replyToMessageId: null,
          },
          currentUserId: profile?.userId,
        })
        .then(async () => {
          analytics.trackOnce(
            STORAGE_KEYS.ANALYTICS_FIRST_ROBI_MESSAGE,
            ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE,
          );
          try {
            const result = await messageService.getMessages({
              conversationId,
              page: 1,
              pageSize: MESSAGES_PAGE_SIZE,
              currentUserId: profile?.userId,
            });
            if (result && result.messages) {
              setMessages(prev => {
                const localGreeting = prev.find(m => String(m._id).startsWith('greeting-'));
                if (localGreeting && !result.messages.some(m => m._id === localGreeting._id)) {
                  return [...result.messages, localGreeting];
                }
                return result.messages;
              });
            }
          } catch (err: unknown) {
            console.error('Failed to fetch updated messages after send:', err);
            const message = getApiErrorMessage(err as ApiError);
            ToastService.show({ type: 'error', message });
          } finally {
            setIsSendingMessage(false);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to send message to API:', err);
          const message = getApiErrorMessage(err as ApiError);
          ToastService.show({ type: 'error', message });
          setMessages(prev => prev.filter(m => m._id !== optimisticId));
          setIsSendingMessage(false);
        });
    },
    [conversationId, currentUser, profile?.userId, isSendingMessage],
  );

  const handleReactionSelect = useCallback(
    (messageId: string | number, reaction: string) => {
      setMessages(prev =>
        prev.map(m => {
          if (m._id !== messageId) return m;
          const nextReaction = m.reaction === reaction ? null : reaction;
          return { ...m, reaction: nextReaction };
        }),
      );
    },
    [],
  );

  const handleDoubleTap = useCallback((msg: ExtendedChatMessage) => {
    setMessages(prev =>
      prev.map(m => {
        if (m._id !== msg._id) return m;
        const nextLiked = !m.isLiked;
        return {
          ...m,
          isLiked: nextLiked,
          reaction: nextLiked
            ? '❤️'
            : m.reaction === '❤️'
              ? null
              : m.reaction,
        };
      }),
    );
  }, []);

  return {
    messages,
    currentUser,
    handleSend,
    handleReactionSelect,
    handleDoubleTap,
    isSendingMessage,
  };
};

export default useRobiChat;
